// ABOUTME: Download menu of the configurator drawer header: CADENAS artifacts + the model's files.
// ABOUTME: Renders nothing when the tenant offers no CADENAS entry and the model carries no attachment.

import {
    CircularProgress,
    Divider,
    DownloadOutlinedIcon,
    IconButton,
    InsertDriveFileOutlinedIcon,
    Menu,
    MenuItem,
    PictureAsPdfOutlinedIcon,
    Tooltip,
    ViewInArOutlinedIcon,
} from '@ds/coolshop';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { CadDownloadEntry, CadDownloadOptions } from '../../hooks/useCadDownload';

// Resolved server-side from the prototype `modelAttachments` setting; `url` is already downloadable
// (`/api/media/<id>` with Content-Disposition: attachment, or the absolute URL of an external link).
export type TAttachment = {
    id: string;
    name: string;
    url: string;
    isImage: boolean;
};

/** What the user picked, as the drawer needs it to ask the server for the artifact. */
export interface CadDownloadSelection {
    /** The tenant format code; the PDF datasheet is one of them. */
    format: string;
}

interface CadDownloadMenuProps {
    options: CadDownloadOptions | null;
    /**
     * The model's own documents, lifted off the resolved product by the drawer. They reach this
     * menu instead of the package's sidebar info button so the drawer offers documents from one
     * place; the model-selection card keeps its own info button, which this never touches.
     */
    attachments: TAttachment[];
    /**
     * The server's CAD verdict for the current configuration, as `isCadDownloadAvailable` reads it
     * off the response's `cadPreview` block. CADENAS entries are disabled while it is false, so the
     * menu offers exactly what the download endpoint would accept — completeness is only one of the
     * reasons it can refuse, and a tenant may deliberately waive it.
     * Attachment entries are never disabled — they belong to the model, not to the configuration.
     */
    cadAvailable: boolean;
    busyEntry: CadDownloadEntry | null;
    onSelect: (selection: CadDownloadSelection) => void;
}

/**
 * The product's download entry in the configurator drawer header.
 *
 * Both sources sit in one menu because they are one thing to the user — "the documents of this
 * product": the formats CADENAS generates for the current configuration, and the static files the
 * model carries. Inside the drawer this is the only place either is offered.
 *
 * Renders nothing at all when the tenant switched no format on for this model and the model has no
 * attachment, so a header without it is the one it has always been.
 */
export function CadDownloadMenu({
    options,
    attachments,
    cadAvailable,
    busyEntry,
    onSelect,
}: CadDownloadMenuProps) {
    const { formatMessage } = useIntl();
    // The anchor is held as state, taken from the click itself, rather than as a ref on the
    // trigger: the trigger is wrapped in a Tooltip, which clones it and attaches a ref of its own,
    // so reading the element back off our own ref is not something to depend on.
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const formats = options?.formats ?? [];

    // Nothing to offer: neither a `cadPreview.download` block switched on for this tenant and model
    // nor a file on the model itself. The header is then the one it has always been.
    if (formats.length === 0 && attachments.length === 0) {
        return null;
    }

    // Only the running entry is disabled, not the whole menu: one format being generated must not
    // hold up the others. The CAD verdict, on the other hand, gates every CADENAS entry.
    const cadDisabled = (entry: CadDownloadEntry) => !cadAvailable || busyEntry === entry;

    // The portal classifies its own formats, so the icon comes from the data rather than from a
    // per-format setting: a drawing for the 2D ones (the PDF datasheet among them), a solid for 3D.
    const formatIcon = (cad: string | undefined) =>
        cad === '3d' ? (
            <ViewInArOutlinedIcon fontSize="small" />
        ) : (
            <PictureAsPdfOutlinedIcon fontSize="small" />
        );

    const triggerLabel = formatMessage({
        id: 'cpq.cad-download.open',
        defaultMessage: 'Download documents',
    });

    return (
        <>
            {/* Same shape as the package's own recap toggle, the control it sits next to in the
                ActionHeader: a tooltipped contained IconButton on the inverted bar palette. */}
            <Tooltip title={triggerLabel} placement="top">
                <IconButton
                    variant="contained"
                    color="primary"
                    invertedColor
                    aria-label={triggerLabel}
                    data-testid="cad-download-button"
                    onClick={event => setAnchorEl(event.currentTarget)}
                >
                    {/* The generation is polled server-side and can take up to a minute and a half,
                        so the trigger carries the progress: the menu closes on click and would
                        otherwise leave nothing on screen. Deliberately not disabled — a second
                        format can be asked for while the first is generating, and the per-entry
                        busy key is what keeps the two apart. */}
                    {busyEntry ? (
                        <CircularProgress size="small" color="inherit" />
                    ) : (
                        <DownloadOutlinedIcon />
                    )}
                </IconButton>
            </Tooltip>
            <Menu
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                onClose={() => setAnchorEl(null)}
                onClick={() => setAnchorEl(null)}
            >
                {formats.map(format => (
                    <MenuItem
                        key={format.code}
                        disabled={!format.available || cadDisabled(format.code)}
                        icon={formatIcon(format.cad)}
                        onClick={() => onSelect({ format: format.code })}
                    >
                        {format.label}
                    </MenuItem>
                ))}
                {/* The model's own files. They are not generated, so they are reachable whatever
                    the configuration resolves to. */}
                {formats.length > 0 && attachments.length > 0 && <Divider />}
                {attachments.map(attachment => (
                    <MenuItem
                        key={attachment.id}
                        icon={<InsertDriveFileOutlinedIcon fontSize="small" />}
                        onClick={() => window.open(attachment.url, '_blank', 'noopener,noreferrer')}
                    >
                        {attachment.name}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
